import { api } from './api'
import {
  MedicationReference,
  PatientMedication,
  PatientMedicationListResponse,
} from '../types/medication'

export interface MedicationSearchFilters {
  search?: string
  atc?: string
  route?: string
  gmsReimbursable?: boolean
  isInterchangeable?: boolean
  iccode?: string
  marketStatus?: string
  dosageForm?: string
}

export interface CreatePatientMedicationInput {
  referenceDrugId?: string | null
  name: string
  dose?: {
    value?: number
    unit?: string
  }
  doseText?: string
  route?: string
  frequency?: {
    code?: string
    text?: string
  }
  quantity?: {
    value?: number
    unit?: string
  }
  startDate?: string
  endDate?: string | null
  indication?: string | null
  orderedFromList?: boolean
  source?: string
}

export interface UpdatePatientMedicationInput extends Partial<CreatePatientMedicationInput> {
  status?: 'active' | 'stopped' | 'completed' | 'error'
}

export const medicationService = {
  /**
   * Search reference medications
   */
  searchReference: async (
    search?: string,
    filters?: Omit<MedicationSearchFilters, 'search'>,
  ): Promise<{ items: MedicationReference[]; totalCount: number }> => {
    const params: any = { ...(filters || {}) }
    if (search) params.search = search

    const response = await api.get<{ items: MedicationReference[]; totalCount: number }>(
      '/medications',
      { params },
    )
    return response.data
  },

  /**
   * Get medications for a patient
   */
  getPatientMedications: async (
    patientId: string,
    params?: { status?: string },
  ): Promise<PatientMedicationListResponse> => {
    const response = await api.get<PatientMedicationListResponse>(
      `/patients/${patientId}/medications`,
      { params },
    )
    return response.data
  },

  /**
   * Create a new patient medication
   */
  createPatientMedication: async (
    patientId: string,
    payload: CreatePatientMedicationInput,
  ): Promise<PatientMedication> => {
    const response = await api.post<PatientMedication>(
      `/patients/${patientId}/medications`,
      payload,
    )
    return response.data
  },

  /**
   * Update an existing patient medication
   */
  updatePatientMedication: async (
    patientId: string,
    medicationId: string,
    payload: UpdatePatientMedicationInput,
  ): Promise<PatientMedication> => {
    const response = await api.put<PatientMedication>(
      `/patients/${patientId}/medications/${medicationId}`,
      payload,
    )
    return response.data
  },

  /**
   * Mark a medication as done in error
   */
  markMedicationError: async (
    patientId: string,
    medicationId: string,
    reason?: string,
  ): Promise<PatientMedication> => {
    const response = await api.post<PatientMedication>(
      `/patients/${patientId}/medications/${medicationId}/mark-error`,
      { reason },
    )
    return response.data
  },

  /**
   * Mark a medication as ordered directly from the list
   */
  orderFromList: async (
    patientId: string,
    medicationId: string,
  ): Promise<PatientMedication> => {
    const response = await api.post<PatientMedication>(
      `/patients/${patientId}/medications/${medicationId}/order`,
      {},
    )
    return response.data
  },
}

